import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  EmptyStation,
  MessageBar,
  type ObjectSchemaDefinition,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  RegisterOAuthClientDocument,
  type RegisterOAuthClientMutation,
  type RegisterOAuthClientMutationVariables,
} from 'generated/axauth-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

interface FormData {
  name: string;
}

type SubmitResponse =
  | RegisterOAuthClientMutation['registerOAuthClient']
  | undefined;

export const oAuthClientCreateSchema = Yup.object<
  ObjectSchemaDefinition<FormData>
>({
  name: Yup.string().label('Name').required().max(200),
});

export const OAuthClientCreate: React.FC = () => {
  const { envId, userStoreId } = useParams<{
    envId: string;
    userStoreId: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const initialFormValues: FormData = {
    name: '',
  };

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const response = (
        await envApolloClient.mutate<
          RegisterOAuthClientMutation,
          RegisterOAuthClientMutationVariables
        >({
          mutation: RegisterOAuthClientDocument,
          variables: {
            input: {
              clientName: formData.name,
              userStoreId,
            },
          },
          fetchPolicy: 'no-cache',
        })
      ).data?.registerOAuthClient;

      return response;
    },
    [envId, userStoreId],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const clientId = submitResponse?.clientId;
      const clientName = submitResponse?.clientSecret;

      if (clientId !== undefined && clientName !== undefined) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients/${clientId}`,
        );
      }
    },
    [envId, history, userStoreId],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="New OAuth Client">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Create<FormData, SubmitResponse>
          title="New OAuth Client"
          validationSchema={oAuthClientCreateSchema}
          saveData={saveData}
          onProceed={onProceed}
          cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients`}
          initialData={{
            data: initialFormValues,
            loading,
          }}
        >
          <Field name="name" label="Name" as={SingleLineTextField} />
        </Create>
      )}
    </>
  );
};

export const OAuthClientCreateCrumb: BreadcrumbResolver = () =>
  'New OAuth Client';
